import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { canUseMarketingTools } from '../config/marketingTools';
import ListingLaunch from '../components/ListingLaunch';

// Marketing Manager toolkit. Resi never stores photos: Eagle Eye photos land in the listing's Canva folder.
const API = '/.netlify/functions/canva-listing-folder';
const card = { background: '#fff', border: '1px solid #e6e1d3', borderRadius: 12, padding: 16, marginBottom: 16 };
const btn = { background: '#111', color: '#D4AF37', border: '1px solid #D4AF37', borderRadius: 8, padding: '10px 14px', minHeight: 44, cursor: 'pointer', fontWeight: 600 };
const QUICK = [
  { label: 'Open Canva Listings folder', href: 'https://www.canva.com/folder/all-designs' },
  { label: 'Open Eagle Eye inbox', href: 'https://mail.google.com/mail/u/?authuser=residencemarketingteam@gmail.com' },
  { label: 'My tasks (invoices)', href: '/tasks' },
];

export default function MarketingToolsPage() {
  const { user, realUser, userRole } = useAuth();
  const tokenUser = realUser || user;
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState('');
  const [params, setParams] = useSearchParams();
  const focus = params.get('listing') || '';

  const call = async (method, body) => {
    const t = await tokenUser.getIdToken();
    const r = await fetch(API, { method, headers: { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' }, body: body ? JSON.stringify(body) : undefined });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || `Error ${r.status}`);
    return j;
  };
  const load = () => call('GET').then(setData).catch((e) => setErr(e.message));
  useEffect(() => { if (user && canUseMarketingTools(userRole)) load(); }, [user, userRole]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!canUseMarketingTools(userRole)) return <div style={{ padding: 24 }}>Marketing Tools are for the marketing manager and admins.</div>;

  const keysFor = (id) => Object.keys(picked[id] || {}).filter((k) => picked[id][k]);
  const create = async (l) => {
    setBusy(l.id); setErr('');
    try { await call('POST', { transactionId: l.id, templateKeys: keysFor(l.id) }); await load(); } catch (e) { setErr(e.message); }
    setBusy('');
  };
  const all = (data?.listings || []).filter((l) => l.address);
  const listings = all.filter((l) => !focus || l.id === focus);
  // Brochure designs first, then the rest in template order.
  const sortDesigns = (ds) => [...(ds || [])].sort((a, b) => (/^brochure/.test(b.key) ? 1 : 0) - (/^brochure/.test(a.key) ? 1 : 0));

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px 16px', boxSizing: 'border-box' }}>
      <div role="note" style={{ background: '#111', color: '#D4AF37', border: '1px solid #D4AF37', borderRadius: 10, padding: '10px 14px', fontWeight: 700, marginBottom: 14 }}>Marketing Manager only</div>
      <h1 style={{ fontSize: 28, margin: '0 0 6px' }}>Marketing Tools</h1>
      <p style={{ color: '#6b6b6b', marginTop: 0 }}>Create a Canva folder for a listing with copies of the templates. Eagle Eye photos arrive in that folder on their own, and Eagle Eye invoices show up as tasks.</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
        {QUICK.map((q) => <a key={q.label} href={q.href} target={q.href.startsWith('http') ? '_blank' : undefined} rel="noreferrer" style={{ ...btn, textDecoration: 'none' }}>{q.label}</a>)}
      </div>
      {data && !data.connected && <div style={{ ...card, borderColor: '#c0392b' }}><strong>Canva not connected.</strong> An admin needs to set: {data.missing.join(', ')}.</div>}
      {err && <div style={{ ...card, borderColor: '#c0392b' }}>{err}</div>}
      <div style={card}>
        <h2 style={{ fontSize: 18, marginTop: 0 }}>Templates (copied, never edited)</h2>
        {!data && !err && <p style={{ margin: 0, color: '#6b6b6b' }}>Loading templates…</p>}
        {data && !(data.templates || []).length && <p style={{ margin: 0, color: '#6b6b6b' }}>No templates set up yet.</p>}
        <ul style={{ margin: 0, paddingLeft: 18 }}>{(data?.templates || []).map((t) => <li key={t.key} style={{ color: t.configured ? undefined : '#6b6b6b' }}>{t.label}{t.configured ? '' : ' (not set up yet)'}</li>)}</ul>
      </div>
      <h2 style={{ fontSize: 18 }}>Listings</h2>
      <label style={{ display: 'block', marginBottom: 12 }}>
        <span style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Property</span>
        <select value={focus} onChange={(e) => setParams(e.target.value ? { listing: e.target.value } : {})} style={{ width: '100%', maxWidth: 480, minHeight: 44, padding: '0 10px' }}>
          <option value="">All listings</option>
          {all.map((l) => <option key={l.id} value={l.id}>{l.address}{l.contactName ? ` - ${l.contactName}` : ''}</option>)}
        </select>
      </label>
      {data && !all.length && <p style={{ color: '#6b6b6b' }}>No listings with an address yet. Listings appear here once an agent submits the seller form with the property address.</p>}
      {!focus && all.length > 0 && <p style={{ color: '#6b6b6b', fontSize: 14 }}>Pick a property above to open its Listing Launch (MLS copy, brochure, feature cards, mailer).</p>}
      {focus && data && !listings.length && <p>That listing has no address yet, or it was not found.</p>}
      {!data && !err && <p>Loading…</p>}
      {listings.map((l) => (
        <div key={l.id} style={card}>
          <div style={{ fontWeight: 700 }}>{l.address}{l.contactName ? ` - ${l.contactName}` : ''}</div>
          <div style={{ color: '#6b6b6b', fontSize: 14, margin: '4px 0 10px' }}>{l.canva?.photosInCanvaAt ? 'Photos in Canva' : l.canva?.folderId ? 'Canva folder ready, waiting for photos' : 'No Canva folder yet'}</div>
          {l.canva?.photosInCanvaAt && <div style={{ fontSize: 14, marginBottom: 8 }}>Eagle Eye photos are already in this listing's Canva folder.</div>}
          {l.canva?.folderId ? (
            <ul style={{ margin: 0, paddingLeft: 18 }}>{sortDesigns(l.canva.designs).map((d) => <li key={d.designId}>{d.editUrl ? <a href={d.editUrl} target="_blank" rel="noreferrer">{d.title}</a> : d.title}</li>)}</ul>
          ) : (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', marginBottom: 10 }}>
                {(data?.templates || []).filter((t) => t.configured).map((t) => (
                  <label key={t.key} style={{ fontSize: 14 }}>
                    <input type="checkbox" checked={!!picked[l.id]?.[t.key]} onChange={(e) => setPicked((p) => ({ ...p, [l.id]: { ...(p[l.id] || {}), [t.key]: e.target.checked } }))} /> {t.label}
                  </label>
                ))}
              </div>
              <button type="button" style={btn} disabled={!data?.connected || busy === l.id} onClick={() => create(l)}>{busy === l.id ? 'Creating…' : 'Create Canva folder'}</button>
            </>
          )}
        </div>
      ))}
      {focus && listings.length > 0 && <ListingLaunch transactionId={focus} />}
    </div>
  );
}
